/**
 * DeleteMessages Component
 * 
 * Component for displaying success and error messages for delete operations.
 * Provides visual feedback after delete operations with dismissible messages.
 * 
 * Features:
 * - Error message display (red with ❌ icon)
 * - Success message display (green with ✅ icon)
 * - Clickable dismiss functionality (click anywhere on message to clear)
 * - Close button (×) for clear visual indication
 * - Conditional rendering (only shows when messages exist)
 * 
 * Used in both office employee and construction worker data display components.
 * 
 * @component
 */

import React from 'react';

/**
 * Props interface for DeleteMessages component.
 */
interface DeleteMessagesProps {
  deleteError: string | null;       // Error message to display (null if no error)
  deleteSuccess: string | null;     // Success message to display (null if no success)
  onClearMessages: () => void;      // Callback function to clear messages
}

/**
 * DeleteMessages Component
 * 
 * Renders success and error messages for delete operations.
 * Messages are dismissible by clicking on them.
 * 
 * @param props - Component props
 * @returns React functional component
 */
export const DeleteMessages: React.FC<DeleteMessagesProps> = ({
  deleteError,
  deleteSuccess,
  onClearMessages
}) => {
  return (
    <>
      {/* Error Message - Displayed when deleteError is not null */}
      {deleteError && (
        <div className="delete-error" onClick={onClearMessages}>
          {/* Error icon and message text */}
          <span>❌ {deleteError}</span>
          {/* Close button indicator */}
          <span className="close-message">×</span>
        </div>
      )}
      
      {/* Success Message - Displayed when deleteSuccess is not null */}
      {deleteSuccess && (
        <div className="delete-success" onClick={onClearMessages}>
          {/* Success icon and message text */}
          <span>✅ {deleteSuccess}</span>
          {/* Close button indicator */}
          <span className="close-message">×</span>
        </div>
      )}
    </>
  );
}; 